import { makeAutoObservable } from "mobx"
import { useFileDragging } from "../Navigator/useFileDragging"
import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { getDownloadURL, ref, uploadBytes } from "firebase/storage"
import { filesStorage } from "../../../services/firebase"
import { v4 } from "uuid"
import {
  ITrip,
  ITripRoom,
  IUser,
  TripRoomGuestID,
  TripSearchResult,
  TripUserPaymentMethod,
  PaymentRequest,
  CabinGradeType,
  PaymentRequestID,
} from "@cruisen/contract"
import { agentManualCabinBook } from "../../../services/firebase-functions/booking.ts"
import {
  getTripUsersPaymentMethods,
  revealCreditCard,
} from "../../../services/firebase-functions/billing.ts"
import { Analytics } from "../../../utils/analytics.ts"
import { currencyToNumber } from "./utils.ts"
import { updateTripRoom } from "../../../services/firestore/rooms.ts"
import { getFakeBookingDetails, getMockedPaymentMethods } from "../../../utils/demoTripLogic.ts"

class BookingModalStore {
  selectedGuestId: TripRoomGuestID
  paymentMethods: TripUserPaymentMethod[]
  filteredPaymentMethods: TripUserPaymentMethod[]
  selectedPaymentMethod: TripUserPaymentMethod | null
  cabinGrade: CabinGradeType | null
  deckId: number
  deckName: string
  cabinNo: string
  currentRoomId: string
  confirmationNumber: string
  approvedPaymentRequest: PaymentRequest | null
  unmaskedPassportNumber: string | null

  constructor(initialRoomId: string, guestId: TripRoomGuestID) {
    this.selectedGuestId = guestId
    this.paymentMethods = []
    this.filteredPaymentMethods = []
    this.selectedPaymentMethod = null
    this.cabinGrade = null
    this.deckId = 0
    this.deckName = ""
    this.cabinNo = ""
    this.currentRoomId = initialRoomId
    this.confirmationNumber = ""
    this.approvedPaymentRequest = null
    this.unmaskedPassportNumber = null

    makeAutoObservable(this)
  }

  setSelectedGuestId(guestId: TripRoomGuestID) {
    this.unmaskedPassportNumber = null
    this.selectedGuestId = guestId
  }

  setPaymentMethods(paymentMethods: TripUserPaymentMethod[]) {
    this.paymentMethods = paymentMethods
  }

  setFilteredPaymentMethods(filteredPaymentMethods: TripUserPaymentMethod[]) {
    this.filteredPaymentMethods = filteredPaymentMethods
  }

  setSelectedPaymentMethod(paymentMethod: TripUserPaymentMethod | null) {
    this.selectedPaymentMethod = paymentMethod
  }

  setDeck(deck: { id: number; name: string }) {
    this.deckId = deck.id
    this.deckName = deck.name
  }

  setCabinGrade(cabinGrade: CabinGradeType) {
    this.cabinGrade = cabinGrade
  }

  setCabinNo(cabinNo: string) {
    this.cabinNo = cabinNo
  }

  setCurrentRoomId(roomId: string) {
    this.unmaskedPassportNumber = null
    this.currentRoomId = roomId
  }

  setConfirmationNumber(confirmationNumber: string) {
    this.confirmationNumber = confirmationNumber
  }

  setApprovedPaymentRequest(approvedPaymentRequest: PaymentRequest | null) {
    this.approvedPaymentRequest = approvedPaymentRequest
  }

  setUnmaskedPassportNumber(unmaskedPassportNumber: string | null) {
    this.unmaskedPassportNumber = unmaskedPassportNumber
  }
}

export const useBookingLogic = ({
  initialRoomId,
  closeModal,
  activeTrip,
  loggedInUser,
}: {
  initialRoomId: string
  closeModal: () => void
  activeTrip: ITrip
  loggedInUser: IUser
}) => {
  const [confirmationFile, setFile] = useState<File | null>(null)

  const store = useMemo(() => new BookingModalStore(initialRoomId, ""), [])
  const queryClient = useQueryClient()
  const isDemoTrip = activeTrip?.tags?.includes("demo")

  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } = useQuery({
    queryKey: ["tripUsersPaymentMethods", activeTrip.id],
    queryFn: async () => {
      if (isDemoTrip) {
        return await Promise.resolve(getMockedPaymentMethods())
      }
      return await getTripUsersPaymentMethods({ tripId: activeTrip.id })
    },
    staleTime: 0,
  })

  const setConfirmationFile = (file: File) => {
    const bytesToMb = 1024 * 1024
    if (file.size > 25 * bytesToMb) {
      alert("File size is too large. Max size for booking confirmation file is 25MB.")
      return
    }

    setFile(file)
  }

  const uploadFile = async (file: File, filesUrl: string, loggedInUser: IUser) => {
    const imageRef = ref(filesStorage, `${filesUrl}${file.name + v4()}`)
    const metadata = {
      customMetadata: {
        username: `${loggedInUser.firstName} ${loggedInUser.lastName}`,
        userId: loggedInUser.id,
        fileName: file.name,
        type: "booking-confirmation",
      },
    }

    try {
      const snapshot = await uploadBytes(imageRef, file, metadata)
      const fileUrl = await getDownloadURL(snapshot.ref)

      queryClient.invalidateQueries({ queryKey: ["navigatorFiles", activeTrip.id] })

      return {
        url: fileUrl,
        type: file.type.startsWith("image/") ? "image" : "pdf",
        filename: file.name,
      }
    } catch (error) {
      console.error("Error uploading file", error)
    }
  }

  useEffect(() => {
    if (paymentMethods?.length > 0) {
      store.setPaymentMethods(paymentMethods ?? [])
      store.setSelectedPaymentMethod(paymentMethods?.[0] ?? null)
    }
  }, [paymentMethods?.length])

  const { mutate: revealSelectedPaymentMethod, isPending: isPaymentMethodRevealing } = useMutation({
    mutationFn: async () => {
      if (!store.selectedPaymentMethod?.tokenizedCreditCard?.id || isDemoTrip) return

      const creditCard = await revealCreditCard({
        creditCardId: store.selectedPaymentMethod.tokenizedCreditCard.id,
      })

      if (
        creditCard &&
        store.selectedPaymentMethod.tokenizedCreditCard.tokenId === creditCard.tokenId
      ) {
        store.setSelectedPaymentMethod({
          ...store.selectedPaymentMethod,
          creditCard,
        })
      }
    },
  })

  const { mutate: bookRoom, isPending: isRoomBooking } = useMutation({
    mutationFn: async (data: {
      tripSearchResult: TripSearchResult
      tripRoom: ITripRoom
      cabinFare: string
      taxesAndFees: string
      cabinGrade: CabinGradeType
      paymentRequestId: PaymentRequestID
    }) => {
      if (isDemoTrip) {
        const { paymentRequest, bookingDetails } = getFakeBookingDetails()

        await updateTripRoom(activeTrip.id, store.currentRoomId, {
          bookingDetails,
          [`paymentRequests.${paymentRequest.id}`]: paymentRequest,
        })
        closeModal()
        return
      }

      const filesUrl = `files/${activeTrip.id}/booking-confirmations/`
      const uploadedConfirmationFile = await uploadFile(confirmationFile, filesUrl, loggedInUser)
      console.log("booking confirmation file", uploadedConfirmationFile)

      const booking = await agentManualCabinBook({
        cabinNo: store.cabinNo,
        totalPrice: (
          currencyToNumber(data.cabinFare) + currencyToNumber(data.taxesAndFees)
        ).toString(),
        cabinFare: data.cabinFare,
        taxesAndFees: data.taxesAndFees,
        confirmationFile: uploadedConfirmationFile,
        currency: "USD",
        deckId: store.deckId,
        deckName: store.deckName,
        reservationCode: store.confirmationNumber,
        tripId: data.tripSearchResult.tripId,
        tripSearchId: data.tripSearchResult.tripSearchId,
        tripSearchResultId: data.tripSearchResult.id,
        tripRoomId: store.currentRoomId,
        cabinGrade: data.cabinGrade,
        paymentRequestId: data.paymentRequestId,
      })

      if (booking) {
        Analytics.logBookCruise({
          tripId: activeTrip.id,
          cruiseId: data.tripSearchResult.id,
        })
        closeModal()
      }
    },
  })

  const { isDragging } = useFileDragging({
    uploadFile: (file: File) => {
      console.log("uploading file", file)
      setConfirmationFile(file)
    },
  })

  return {
    store,
    isDragging,
    bookRoom,
    setConfirmationFile,
    isRoomBooking,
    confirmationFile,
    isPaymentMethodsLoading,
    revealSelectedPaymentMethod,
    isPaymentMethodRevealing,
  }
}
